import React from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

export const TermsOfServicePage = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto p-6 md:p-10 space-y-6" data-testid="terms-page-public">
      <h1 className="text-3xl font-bold text-slate-800">Terms of Service</h1>
      <p className="text-xs text-slate-400">Last updated: March 2026</p>
      <div className="prose prose-slate prose-sm max-w-none space-y-4 text-sm text-slate-600 leading-relaxed">
        <h2 className="text-lg font-semibold text-slate-700">1. Acceptance of Terms</h2>
        <p>By accessing PeptideProtocols.ai ("the Platform"), operated by Dallas Regenerative LLC, you agree to be bound by these Terms of Service. If you do not agree, do not use the Platform.</p>
        <h2 className="text-lg font-semibold text-slate-700">2. Platform Purpose</h2>
        <p>PeptideProtocols.ai is a clinical decision support tool designed for licensed healthcare practitioners. The Platform provides AI-generated peptide therapy protocol suggestions based on patient data inputs. All output is intended as decision support only and does not constitute medical advice, diagnosis, or treatment.</p>
        <h2 className="text-lg font-semibold text-slate-700">3. User Responsibilities</h2>
        <p>Users must be licensed healthcare practitioners. All clinical decisions remain the sole responsibility of the treating practitioner. Users must independently verify all AI-generated recommendations before clinical application.</p>
        <h2 className="text-lg font-semibold text-slate-700">4. Limitation of Liability</h2>
        <p>Dallas Regenerative LLC provides the Platform "as is" without warranties of any kind. We are not liable for clinical outcomes, treatment decisions, or any damages arising from use of the Platform.</p>
        <h2 className="text-lg font-semibold text-slate-700">5. Data Handling</h2>
        <p>Patient data entered into the Platform is used solely for generating protocol recommendations. See our Privacy Policy for details on data storage and handling.</p>
        <h2 className="text-lg font-semibold text-slate-700">6. Intellectual Property</h2>
        <p>All content, algorithms, and proprietary formulas on the Platform are the property of Dallas Regenerative LLC.</p>
        <h2 className="text-lg font-semibold text-slate-700">7. Contact</h2>
        <p>Questions about these terms: info@peptideprotocols.ai</p>
      </div>
      <Button variant="outline" onClick={() => navigate('/')} className="mt-4">Back to Home</Button>
    </div>
  );
};

export const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto p-6 md:p-10 space-y-6" data-testid="privacy-page-public">
      <h1 className="text-3xl font-bold text-slate-800">Privacy Policy</h1>
      <p className="text-xs text-slate-400">Last updated: March 2026</p>
      <div className="prose prose-slate prose-sm max-w-none space-y-4 text-sm text-slate-600 leading-relaxed">
        <h2 className="text-lg font-semibold text-slate-700">1. Information We Collect</h2>
        <p>We collect information you provide directly: practitioner registration details, patient assessment data (demographics, labs, genetics), and feedback submissions.</p>
        <h2 className="text-lg font-semibold text-slate-700">2. How We Use Information</h2>
        <p>Patient data is used to generate personalized peptide protocol recommendations. Anonymized outcome data may be used to improve our AI models. We do not sell personal data to third parties.</p>
        <h2 className="text-lg font-semibold text-slate-700">3. Data Storage & Security</h2>
        <p>Data is stored on encrypted servers. Access is restricted to authorized personnel. We implement industry-standard security measures to protect your data.</p>
        <h2 className="text-lg font-semibold text-slate-700">4. Data Retention</h2>
        <p>Patient assessment data is retained for the duration of your account. You may request deletion of your data at any time by contacting us.</p>
        <h2 className="text-lg font-semibold text-slate-700">5. Third-Party Services</h2>
        <p>We use AI language model services (OpenAI, Anthropic) for protocol generation. Patient data sent to these services is processed according to their respective privacy policies and data processing agreements.</p>
        <h2 className="text-lg font-semibold text-slate-700">6. Your Rights</h2>
        <p>You have the right to access, correct, or delete your personal data. Contact info@peptideprotocols.ai for data requests.</p>
        <h2 className="text-lg font-semibold text-slate-700">7. Contact</h2>
        <p>Privacy inquiries: info@peptideprotocols.ai | Dallas Regenerative LLC</p>
      </div>
      <Button variant="outline" onClick={() => navigate('/')} className="mt-4">Back to Home</Button>
    </div>
  );
};

export const AmbassadorTermsPage = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto p-6 md:p-10 space-y-6" data-testid="ambassador-terms-page">
      <h1 className="text-3xl font-bold text-slate-800">Ambassador Program Terms</h1>
      <p className="text-xs text-slate-400">Effective: April 2026 | Dallas Regenerative LLC</p>
      <div className="prose prose-slate prose-sm max-w-none space-y-4 text-sm text-slate-600 leading-relaxed">
        <h2 className="text-lg font-semibold text-slate-700">1. Commission Structure</h2>
        <p>The PeptideProtocols.ai Ambassador Program offers four tiers with the following commission rates on referred practitioner subscriptions:</p>
        <ul>
          <li><strong>Founding Physician:</strong> Up to 49% commission</li>
          <li><strong>Contributing Practitioner:</strong> Up to 30% commission</li>
          <li><strong>Distributor:</strong> 25% commission</li>
          <li><strong>Influencer:</strong> 15% commission</li>
        </ul>

        <h2 className="text-lg font-semibold text-slate-700">2. How Commissions Are Calculated</h2>
        <p>Commission is calculated as a percentage of the net subscription revenue from practitioners you refer. Net revenue is the subscription amount after any discount applied to the referred practitioner.</p>

        <h2 className="text-lg font-semibold text-slate-700">3. Discount Rule</h2>
        <p><strong>Any discount you offer to referred practitioners comes directly from your commission.</strong> For example, if you earn 25% commission and offer a 10% discount to a referral, your effective commission becomes 15% on that subscription. Your discount cannot exceed your tier's maximum discount allowance.</p>

        <h2 className="text-lg font-semibold text-slate-700">4. Payout Terms</h2>
        <ul>
          <li>Minimum payout threshold: <strong>$50</strong></li>
          <li>Payout frequency: <strong>Monthly</strong> (processed on the 1st of each month for the prior month's earnings)</li>
          <li>Payment method: <strong>Stripe Connect</strong> (you must connect a Stripe account to receive payouts)</li>
          <li>Commissions are earned when the referred practitioner's payment is successfully processed</li>
        </ul>

        <h2 className="text-lg font-semibold text-slate-700">5. Conduct Expectations</h2>
        <ul>
          <li>Do not use spam, unsolicited bulk messaging, or misleading claims when promoting PeptideProtocols.ai</li>
          <li>Do not make medical claims about the platform's AI-generated protocols</li>
          <li>Accurately represent the platform's capabilities and pricing</li>
          <li>Do not impersonate PeptideProtocols.ai staff or claim official endorsement beyond your ambassador status</li>
          <li>Comply with all applicable advertising and marketing regulations</li>
        </ul>

        <h2 className="text-lg font-semibold text-slate-700">6. Termination</h2>
        <p>Dallas Regenerative LLC reserves the right to terminate your ambassador status at any time for violation of these terms, fraudulent activity, or at our discretion. Upon termination, pending commissions above the minimum threshold will be paid out within 30 days. Future commissions will cease immediately.</p>

        <h2 className="text-lg font-semibold text-slate-700">7. Modifications</h2>
        <p>We may modify these terms at any time. Active ambassadors will be notified of material changes via email. Continued participation after notification constitutes acceptance of updated terms.</p>

        <h2 className="text-lg font-semibold text-slate-700">8. Contact</h2>
        <p>Ambassador program inquiries: info@peptideprotocols.ai | Dallas Regenerative LLC</p>
      </div>
      <Button variant="outline" onClick={() => navigate('/')} className="mt-4">Back to Home</Button>
    </div>
  );
};

